import React, { useState } from "react";
import {
  IconButton,
  Typography,
  Box,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  MoreVert as MoreVertIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const FileExplorerHeader = ({
  onBackClick,
  title,
  leftContent,
  rightContent,
  containerProps = {},
}) => {
  const { t } = useTranslation();
  const translation = t("fileExplorer");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Use custom breakpoint at 1000px instead of default 'md' (960px)
  const isTablet = useMediaQuery("(max-width: 1000px)");

  const [menuAnchor, setMenuAnchor] = useState(null);
  const isMenuOpen = Boolean(menuAnchor);

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // Clone right content children to extract them for menu
  const getRightContentElements = () => {
    if (!rightContent) return [];

    const children = React.Children.toArray(
      rightContent.props?.children || rightContent
    );
    return children.map((child, index) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, { key: index });
      }
      return child;
    });
  };

  const rightContentElements = getRightContentElements();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        height: "100%",
        px: { xs: 1, sm: 2 },
        py: { xs: 0.5, sm: 0 },
        minHeight: { xs: 48, sm: 56 },
        gap: { xs: 0.5, sm: 1 },
        ...containerProps.sx,
      }}
      {...containerProps}
    >
      {/* Left Section - Always visible: Back button + Breadcrumb/Title */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth: 0,
          flex: 1,
          mr: { xs: 0.5, sm: 1 },
          overflow: "hidden",
          pb: { xs: 0.5, sm: 0 },
        }}
      >
        {onBackClick && (
          <IconButton
            onClick={onBackClick}
            sx={{
              mr: { xs: 0.5, sm: 1 },
              p: { xs: 0.5, sm: 1 },
              flexShrink: 0,
              minWidth: 36,
              minHeight: 36,
              zIndex: 2,
              backgroundColor: "background.paper",
              position: "relative",
            }}
            aria-label={translation.backToFiles}
            size="small"
          >
            <ArrowBackIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        )}
        {/* Title/Breadcrumb Section */}
        <Box
          sx={{
            minWidth: 0,
            flex: 1,
            overflow: "hidden",
            mr: 2,
            zIndex: 1,
          }}
        >
          {typeof title === "string" ? (
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: 14, sm: 16, md: 18, lg: 20 },
                fontWeight: 600,
                color: "text.primary",
                lineHeight: 1.2,
                mb: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
              component="span"
              title={
                translation[title.replace(/\s/g, "").toLowerCase()] || title
              }
            >
              {translation[title.replace(/\s/g, "").toLowerCase()] || title}
            </Typography>
          ) : (
            <Box
              sx={{
                overflow: "hidden",
                "& .MuiBreadcrumbs-root": {
                  fontSize: { xs: "0.85rem", sm: "0.95rem" },
                  "& .MuiTypography-root": {
                    fontSize: "inherit",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: { xs: "80px", sm: "120px", md: "none" },
                  },
                  "& .MuiBreadcrumbs-separator": {
                    mx: { xs: 0.25, sm: 0.5 },
                  },
                  "& .MuiBreadcrumbs-li": {
                    overflow: "hidden",
                  },
                },
              }}
            >
              {title}
            </Box>
          )}
        </Box>
        {/* Left Content - Always show if available */}
        {leftContent && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              ml: 1,
              zIndex: 2,
            }}
          >
            {leftContent}
          </Box>
        )}
      </Box>

      {/* Right Section - Desktop: Show all, Mobile/Tablet: Show menu */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          gap: { xs: 0.5, sm: 1 },
        }}
      >
        {rightContent && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: { xs: "wrap", sm: "nowrap" },
              "& .MuiButton-root": {
                minWidth: "64px",
                px: 1.5,
                fontSize: "0.875rem",
              },
              "& .MuiIconButton-root": {
                p: 1,
              },
              "& .MuiTextField-root": {
                minWidth: "150px",
              },
            }}
          >
            {rightContent}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FileExplorerHeader;
